import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { BriefcaseIcon, Calendar, FileCheck, Newspaper, AlertCircle } from "lucide-react";
import { Link } from "wouter";

export default function ClientDashboard() {
  const { user } = useAuth();

  return (
    <DashboardLayout pageTitle="My Dashboard">
      {/* Welcome Section */}
      <section className="mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Welcome back, {user?.fullName || "Client"}</CardTitle>
            <CardDescription>
              Here's an overview of your job search progress and upcoming appointments.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <Button asChild variant="outline" className="flex items-center gap-2">
                <Link href="/dashboard/client/profile">
                  <span>View My Profile</span>
                </Link>
              </Button>
              <Button asChild className="flex items-center gap-2">
                <Link href="/dashboard/client/jobs">
                  <BriefcaseIcon className="w-4 h-4" />
                  <span>Browse Jobs</span>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Action Items */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Action Items</h2>
        <Alert className="mb-4 border-amber-500 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <AlertTitle className="text-amber-800">Attention Needed</AlertTitle>
          <AlertDescription className="text-amber-800">
            Please complete your resume information to help us match you with suitable jobs.
          </AlertDescription>
        </Alert>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary-600" />
                <CardTitle className="text-base">Upcoming Appointment</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="font-medium">Resume Review with Sarah Thompson</p>
              <p className="text-sm text-slate-500">Tomorrow at 10:00 AM</p>
              <div className="mt-4">
                <Button variant="outline" size="sm">Reschedule</Button>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-primary-600" />
                <CardTitle className="text-base">Documents Needed</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500">Please upload the following:</p>
              <ul className="list-disc list-inside text-sm text-slate-500 mt-2">
                <li>ID or Driver's License</li>
                <li>Proof of address</li>
                <li>Work authorization documents</li>
              </ul>
              <div className="mt-4">
                <Button size="sm">Upload Documents</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Recent Jobs & Resources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section>
          <h2 className="text-xl font-semibold mb-4">Recent Job Matches</h2>
          <div className="space-y-4">
            {[1, 2, 3].map((job) => (
              <Card key={job}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium">Warehouse Associate</h3>
                      <p className="text-sm text-slate-500">Metro Logistics • Full-time</p>
                      <p className="text-sm text-slate-500">Chicago, IL • $18-20/hr</p>
                    </div>
                    <Button size="sm" variant="outline">View</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button variant="outline" className="w-full">View All Job Matches</Button>
          </div>
        </section>
        
        <section>
          <h2 className="text-xl font-semibold mb-4">Resources & Training</h2>
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <Newspaper className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Interview Preparation Guide</h3>
                    <p className="text-sm text-slate-500">Learn how to impress potential employers</p>
                    <Button variant="link" className="px-0">Read Guide</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <Newspaper className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Resume Writing Workshop</h3>
                    <p className="text-sm text-slate-500">Virtual workshop on Thursday at 6PM</p>
                    <Button variant="link" className="px-0">Register</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <Newspaper className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium">Housing Assistance Programs</h3>
                    <p className="text-sm text-slate-500">Resources for stable housing options</p>
                    <Button variant="link" className="px-0">Learn More</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}